import { oneDecimalLabel } from "@hakili/domain-types";
import { formatQuantity, t } from "@hakili/i18n";
import { BANK_FS_META, BANK_TEXT, BANK_TEXT_2 } from "@hakili/ui";
import { AlertTriangle, Check, ChevronRight } from "lucide-react";
import type { ReactElement } from "react";

import { D2Card, D2Chip } from "./d2-parts.js";
import { fill } from "./dossiers.js";

/**
 * D2 frame 62, part b (`chef.js:29-48` `screenDecision`; `components.css:188-192` `.axis`, `:252` `.axis .m.beyond`, `:117-120` `.tbl`; frames 161 and 162): « Le taux face à la politique ».
 *
 * **The browser places and prints; it computes and compares nothing (D-1020-7).** The door serves the axis (`axis`: the window's ends and ticks as rates, the tolerance zone and each
 * marker as a position 0 to 100, D2's words, the side a rate went beyond) and the table's rows (`controls[]`: the proposed value, the policy value it is held against, a typed
 * state, and on a rate gap the gap in points). A position is a CSS `left`; a label is printed as served; a rate or a figure goes through the shared formatter. `axis: null` draws
 * no axis. This file imports nothing from `decisions.tsx` (the boundaries check's `no-circular-modules`): the shapes are its own and the file view is assignable to them.
 */
export interface RateAxisMarkerView {
  readonly kind: "grid" | "proposed" | "gridAndProposed";
  readonly label: string;
  readonly posPct: number;
  readonly beyond: "left" | "right" | null;
}

export interface RateAxisView {
  readonly min: string;
  readonly max: string;
  readonly ticks: readonly [string, string, string];
  readonly zone: { readonly startPos: number; readonly endPos: number };
  readonly markers: readonly RateAxisMarkerView[];
}

export interface PolicyControlView {
  readonly code: string;
  readonly proposed: number | string;
  readonly policy: Readonly<Record<string, number | string>>;
  readonly state: "ok" | "ecart" | "bloquant";
  /** On the rate control, and only on an `ecart`: the gap to the grid in points, served (D2's « Écart 0,3 point »). */
  readonly gapPts?: number;
}

export interface PolicyGapRowView {
  readonly code: string;
  readonly label: string;
  readonly proposed: string;
  readonly limit: string;
  readonly level: "comite" | "chef";
}

export interface PolicyFileView {
  readonly amountFinanced: number;
  /** The rate the analyst typed: D2's « Taux saisi » where the grid has no rate for the letter. */
  readonly nominalRatePct: string;
  /** The server's gaps: `gridRateMissing` carries its own words (`detail`), and a row stands for it where no rate control is served. */
  readonly gaps: readonly { readonly code: string; readonly detail: string }[];
  readonly delegationThresholdAmount: number | null;
  readonly requiresCommittee: boolean;
  readonly controls: readonly PolicyControlView[];
  /** `#772`: the gaps with no control row, drawn as warn rows after « Montant ». */
  readonly gapRows?: readonly PolicyGapRowView[];
  /** `#772`: why the committee sits, in D2's words; the « Montant » chip reads them. */
  readonly committeeReasons?: readonly { readonly code: string; readonly words: string }[];
  readonly axis: RateAxisView | null;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;

/** The served axis, parsed at the boundary: a block that is not whole is no axis (a position is never invented). */
export function axisFrom(raw: unknown): RateAxisView | null {
  const axis = asRecord(raw);
  if (axis === null) return null;
  const min = axis["min"];
  const max = axis["max"];
  const ticks = axis["ticks"];
  const zone = asRecord(axis["zone"]);
  const markers = axis["markers"];
  if (typeof min !== "string" || typeof max !== "string" || zone === null) return null;
  if (!Array.isArray(ticks) || ticks.length !== 3) return null;
  const [first, second, third] = ticks as unknown[];
  if (typeof first !== "string" || typeof second !== "string" || typeof third !== "string") return null;
  if (typeof zone["startPos"] !== "number" || typeof zone["endPos"] !== "number") return null;
  if (!Array.isArray(markers)) return null;
  const parsed: RateAxisMarkerView[] = [];
  for (const one of markers) {
    const marker = asRecord(one);
    const kind = marker?.["kind"];
    const label = marker?.["label"];
    const posPct = marker?.["posPct"];
    const beyond = marker?.["beyond"];
    if (kind !== "grid" && kind !== "proposed" && kind !== "gridAndProposed") return null;
    if (typeof label !== "string" || typeof posPct !== "number") return null;
    parsed.push({
      kind,
      label,
      posPct,
      beyond: beyond === "left" || beyond === "right" ? beyond : null,
    });
  }
  return {
    min,
    max,
    ticks: [first, second, third],
    zone: { startPos: zone["startPos"], endPos: zone["endPos"] },
    markers: parsed,
  };
}

/** A rate as the door spells it (« 10.7 ») in the French way (« 10,7 »). */
const rateLabel = (rate: string): string => rate.replace(".", ",");
const pct = (rate: string): string => fill("bank.decision.pct", { n: rateLabel(rate) });

/** D2 `.axis`: the grey line, the tolerance zone, the markers with their words above, the three ticks under. */
function RateAxisFigure(props: { readonly axis: RateAxisView }): ReactElement {
  const { axis } = props;
  return (
    <div className="relative mx-10 mb-9 mt-[34px] h-24" aria-hidden="true" data-testid="bank-decision-axe">
      <div className="absolute inset-x-0 top-[46px] h-1 rounded-[3px] bg-[color:var(--hk-axis-track)]" />
      <div
        className="absolute top-[38px] h-5 rounded-md border border-dashed border-[color:var(--hk-axis-zone-line)] bg-[color:var(--hk-warn-bg)]"
        style={{ left: `${String(axis.zone.startPos)}%`, right: `calc(100% - ${String(axis.zone.endPos)}%)` }}
        data-testid="bank-decision-axe-zone"
      >
        <span
          className={`absolute left-0 top-[26px] whitespace-nowrap ${BANK_FS_META} text-[color:var(--hk-warn)]`}
        >
          {t("bank.decision.zoneTolerance")}
        </span>
      </div>
      {axis.markers.map((one) => {
        const proposed = one.kind === "proposed";
        const beyond = one.beyond !== null;
        return (
          <div
            key={one.kind}
            data-marque={one.kind}
            {...(one.beyond === null ? {} : { "data-beyond": one.beyond })}
            className={`absolute border-l-[3px] ${beyond ? "-top-3.5" : "top-2"} bottom-2 ${
              proposed ? "border-[color:var(--hk-warn)]" : "border-[color:var(--hk-text)]"
            }`}
            style={{ left: `${String(one.posPct)}%` }}
          >
            <b
              className={`absolute -top-2 whitespace-nowrap font-[family-name:var(--hk-mono)] ${BANK_FS_META} font-semibold leading-[normal] ${
                proposed ? "text-[color:var(--hk-warn)]" : ""
              } ${
                one.beyond === "right"
                  ? "-translate-x-full -translate-y-full"
                  : one.beyond === "left"
                    ? "-translate-y-full -ml-px"
                    : "-translate-x-1/2 -translate-y-full ml-px"
              }`}
            >
              {one.label}
            </b>
          </div>
        );
      })}
      <div
        className={`absolute inset-x-0 -bottom-4 flex justify-between font-[family-name:var(--hk-mono)] ${BANK_FS_META} leading-[normal] ${BANK_TEXT_2}`}
        data-testid="bank-decision-axe-graduations"
      >
        {axis.ticks.map((tick, index) => (
          <span key={`${String(index)}-${tick}`}>{rateLabel(tick)}</span>
        ))}
      </div>
    </div>
  );
}

interface Row {
  readonly code: string;
  readonly label: string;
  readonly proposed: string;
  readonly policy: string;
  readonly chip: { readonly tone: "ok" | "warn" | "info"; readonly words: string };
}

const num = (value: number | string | undefined): string => (value === undefined ? "" : String(value));

/** One served control as D2's row: the words are the catalogue's, every figure is the door's. */
function rowOf(control: PolicyControlView): Row | null {
  const { policy, state } = control;
  const good = state === "ok";
  const tone = good ? ("ok" as const) : ("warn" as const);
  switch (control.code) {
    case "rate":
      return {
        code: "rate",
        label: t("bank.decision.tauxNominal"),
        proposed: pct(num(control.proposed)),
        policy: fill("bank.decision.politiqueTaux", {
          note: num(policy["grade"]),
          grille: rateLabel(num(policy["gridRatePct"])),
          tolerance: rateLabel(num(policy["rateTolerancePct"])),
        }),
        chip: {
          tone,
          words:
            good || control.gapPts === undefined
              ? t(good ? "bank.decision.dansLaGrille" : "bank.decision.etatEcart")
              : fill("bank.decision.ecartPoint", { n: rateLabel(oneDecimalLabel(control.gapPts)) }),
        },
      };
    case "duration":
      return {
        code: "duration",
        label: t("bank.decision.duree"),
        proposed: fill("bank.composer.mois", { n: num(control.proposed) }),
        policy: fill("bank.decision.politiqueDuree", { n: num(policy["maxMonths"]) }),
        chip: { tone, words: t(good ? "bank.decision.dansLaPolitique" : "bank.decision.auDessus") },
      };
    case "taeg":
      return {
        code: "taeg",
        label: t("bank.decision.taeg"),
        proposed: pct(num(control.proposed)),
        policy: fill("bank.decision.politiqueTaeg", {
          n: rateLabel(oneDecimalLabel(Number(policy["ceilingPct"]))),
        }),
        chip: { tone, words: t(good ? "bank.decision.sousLePlafond" : "bank.decision.depasse") },
      };
    case "capacity":
      return {
        code: "capacity",
        label: t("bank.decision.partCapacite"),
        proposed: pct(oneDecimalLabel(Number(control.proposed))),
        policy: fill("bank.decision.politiqueCapacite", { n: num(policy["limitPct"]) }),
        chip: { tone, words: t(good ? "bank.decision.dansLaQuotite" : "bank.decision.depasse") },
      };
    case "ltv":
      return {
        code: "ltv",
        label: t("bank.decision.ltv"),
        proposed: pct(rateLabel(num(control.proposed))),
        policy: fill("bank.decision.politiqueLtv", { n: num(policy["maxPct"]) }),
        chip: { tone, words: t(good ? "bank.decision.dansLaPolitique" : "bank.decision.depasse") },
      };
    default:
      return null;
  }
}

const CHIP_ICON = {
  ok: <Check size={14} strokeWidth={2.4} aria-hidden="true" />,
  warn: <AlertTriangle size={14} strokeWidth={2.4} aria-hidden="true" />,
  info: <ChevronRight size={14} strokeWidth={2.4} aria-hidden="true" />,
} as const;

const TH =
  "border-y border-[color:var(--hk-line-soft)] bg-[color:var(--hk-surface-2)] px-5 py-2.5 text-left text-[length:var(--hk-fs-micro)] font-semibold uppercase leading-[normal] tracking-[.08em] text-[color:var(--hk-text-2)]";
const TD = "px-5 py-3 align-middle";
const TD_RULE = "border-b border-[color:var(--hk-line-soft)]";

/** D2's « Contrôle / Proposé / Politique / État », the served controls only (a control with no policy value is absent), then « Montant » against the delegation. */
function ControlsTable(props: { readonly file: PolicyFileView }): ReactElement {
  const { file } = props;
  const rows = file.controls.flatMap((one) => {
    const row = rowOf(one);
    return row === null ? [] : [row];
  });
  // D2 `chef.js:44`: the served words, joined « et ».
  const reasons = (file.committeeReasons ?? []).map((one) => one.words).join(t("bank.decision.etMot"));
  const delegation: Row | null =
    file.delegationThresholdAmount === null
      ? null
      : {
          code: "delegation",
          label: t("bank.decision.montant"),
          proposed: formatQuantity(file.amountFinanced),
          policy: fill("bank.decision.politiqueDelegation", {
            montant: formatQuantity(file.delegationThresholdAmount),
          }),
          chip: {
            tone: "info",
            words: !file.requiresCommittee
              ? t("bank.decision.dansVotreDelegation")
              : reasons === ""
                ? t("bank.decision.comiteApresAccord")
                : fill("bank.decision.comiteApresAccordMotif", { motif: reasons }),
          },
        };
  // D2 `chef.js:36` (`g.noRate`): a letter with no grid row is ONE row, « Grille tarifaire », in the server's words; it stands where the rate control is absent.
  const missing = file.gaps.find((one) => one.code === "gridRateMissing");
  const noRate: Row | null =
    missing === undefined || rows.some((one) => one.code === "rate")
      ? null
      : {
          code: "gridRateMissing",
          label: t("bank.decision.grilleTarifaire"),
          proposed: fill("bank.decision.tauxSaisi", { taux: rateLabel(file.nominalRatePct) }),
          policy: missing.detail,
          chip: { tone: "warn", words: t("bank.decision.ecartAucunTaux") },
        };
  // Frame 170: the gaps with no control row, after « Montant », warn rows at the served level.
  const gapRows: Row[] = (file.gapRows ?? []).map((one) => ({
    code: one.code,
    label: one.label,
    proposed: one.proposed,
    policy: one.limit,
    chip: {
      tone: "warn",
      words: t(one.level === "comite" ? "bank.decision.ecartNiveauComite" : "bank.decision.ecartNiveauChef"),
    },
  }));
  const all = [
    ...(noRate === null ? [] : [noRate]),
    ...rows,
    ...(delegation === null ? [] : [delegation]),
    ...gapRows,
  ];
  return (
    <table
      className={`w-full border-collapse text-[length:var(--hk-fs-read)] ${BANK_TEXT}`}
      aria-label={t("bank.decision.politiqueTitre")}
      data-testid="bank-decision-politique-tableau"
    >
      <thead>
        <tr>
          <th className={TH}>{t("bank.decision.colControle")}</th>
          <th className={TH}>{t("bank.decision.colPropose")}</th>
          <th className={TH}>{t("bank.decision.colPolitique")}</th>
          <th className={TH}>{t("bank.decision.colEtat")}</th>
        </tr>
      </thead>
      <tbody>
        {all.map((row, index) => {
          const rule = index === all.length - 1 ? "" : ` ${TD_RULE}`;
          return (
            <tr key={row.code} data-testid={`bank-decision-politique-${row.code}`}>
              <td className={`${TD}${rule}`}>{row.label}</td>
              <td className={`${TD}${rule} font-[family-name:var(--hk-mono)] text-[0.94em] font-bold`}>
                {row.proposed}
              </td>
              <td className={`${TD}${rule}`}>{row.policy}</td>
              <td className={`${TD}${rule}`}>
                <D2Chip wrap tone={row.chip.tone === "info" ? "brand" : row.chip.tone}>
                  {CHIP_ICON[row.chip.tone]}
                  {row.chip.words}
                </D2Chip>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** The card: D2's `card("Le taux face à la politique", axis + table, { flush: true })`. */
export function PolicyCard(props: { readonly file: PolicyFileView }): ReactElement {
  return (
    <D2Card title={t("bank.decision.politiqueTitre")} flush testId="bank-decision-politique">
      {props.file.axis === null ? null : <RateAxisFigure axis={props.file.axis} />}
      <ControlsTable file={props.file} />
    </D2Card>
  );
}
